/**
 * `<PendingApprovalsPanel>` — the staged money/send queue for ONE operator lane,
 * approvable from the dashboard instead of only from Telegram.
 *
 * Mounted twice, never once: `lane="peter"` on `/pr`, `lane="inbox"` on
 * `/inbox`. `operator_ref` is REQUIRED by `list_pa_pending_actions` (it is the
 * per-agent isolation), so one call serves one lane; a single `/home` panel
 * would have to fan out across `trainerops`, `stripe_ops` and the staff-UUID PA
 * lanes as well, which is a different feature.
 *
 * This runs ALONGSIDE the Telegram tap, not instead of it — Mario's decision.
 * That is precisely why `pa-drain-truth` had to ship first: two surfaces racing
 * for one row is the situation where the bridge's unconditional "🚫 Rejected"
 * marker turned a stale tap into a send nobody wanted.
 *
 * FOUR RULES THIS COMPONENT MUST NOT LOSE (each one silent when broken):
 *
 *  1. **Never pin the wire.** `dispatchAction` mints a fresh uuid-v7 per call.
 *     `list_pa_pending_actions` declares `responsePiiPaths` over `summary`,
 *     `action_input`, `reason` AND `confirmation_token`, so a pinned
 *     `requestId` gives one good read then blank cards with dead buttons
 *     forever. On `resolve_pa_action` the same "optimisation" is worse:
 *     `deriveIdempotencyKey` hashes the WIRE, so Approve-then-Reject on one row
 *     would short-circuit to the cached APPROVE with `ok: true`. Double-submit
 *     safety is the disabled button below plus a refetch.
 *  2. **Approve is a confirm step, not a click.** It sends real mail with no
 *     undo and `useDispatch` has no double-submit guard of its own.
 *  3. **No multi-select.** The drain sends newest-first, five per beat; a bulk
 *     approve of twenty leaves over ~40 minutes in reverse review order with no
 *     stop control.
 *  4. **An inbox reject is reject + feedback, in that order, or nothing.** See
 *     `inboxRejectBlockedReason`.
 *
 * Deliberately NOT offered: a cancel for an already-approved row. Nothing can
 * pull a row out of `approved` — the daily sweep only touches `pending` — so
 * the only lever is closing the engagement/matter and letting the drain's
 * pre-send re-check park it, which is best-effort and racy. That is documented
 * in the runbook as an operator recipe, not dressed up as a button here.
 */

import { useCallback, useEffect, useState } from "react";
import { dispatchAction } from "../lib/dispatch";
import { useDispatch } from "../lib/use-dispatch";
import {
  approvalLiveness,
  buildPendingListInput,
  buildResolveInput,
  draftPreview,
  humanAge,
  inboxRejectBlockedReason,
  isExpired,
  mayRecordFeedback,
  parseInboxThreadRef,
  type ApprovalLane,
  type DrainLivenessWire,
  type PendingApprovalRow,
} from "../lib/pa-approvals";
import { studioFormat } from "../lib/studio-tz";

const DATE_OPTS: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

function fmt(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : studioFormat(DATE_OPTS).format(d);
}

export function PendingApprovalsPanel({
  lane,
  drain,
  onResolved,
}: {
  readonly lane: ApprovalLane;
  /** From the board payload — `null` while the board is still loading. */
  readonly drain: DrainLivenessWire | null;
  /** Let the host page refetch: a resolve can move a matter's stage. */
  readonly onResolved?: () => void;
}): JSX.Element {
  const [rows, setRows] = useState<ReadonlyArray<PendingApprovalRow> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState<number>(() => Date.now());
  /**
   * The race notice lives HERE, not on the card, and the render walk is why.
   * When a resolve loses the race the card refetches, the row is gone, and the
   * card unmounts — taking any message it was holding with it. The operator saw
   * the row silently vanish, which is the one reading this panel must never
   * allow: "it disappeared" is indistinguishable from "it was sent".
   */
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback((): void => {
    // NO `options` argument — see rule 1. The default fresh request_id is what
    // keeps every refetch a real read rather than a PII-stripped replay.
    void dispatchAction<
      Record<string, unknown>,
      { pending_actions: ReadonlyArray<PendingApprovalRow> }
    >("list_pa_pending_actions", buildPendingListInput(lane)).then((r) => {
      setNow(Date.now());
      if (r.ok) {
        setRows(r.response.pending_actions);
        setError(null);
      } else {
        setRows([]);
        setError(`${r.message} (${r.code})`);
      }
    });
  }, [lane]);

  useEffect(() => {
    load();
  }, [load]);

  const liveness = approvalLiveness(drain, now);
  const pending = rows ?? [];

  return (
    <div className="pa-approvals">
      <div className="pr-section-head">
        <h2 className="pr-heading">
          Waiting for you{pending.length > 0 ? ` (${pending.length})` : ""}
        </h2>
        <button
          type="button"
          className="row-action-btn"
          onClick={() => {
            setNotice(null);
            load();
          }}
        >
          Refresh
        </button>
      </div>

      {/* Shown in EVERY state, including the healthy one — the age is the
          point. The capability's own window is 26h, so a drain dead for twenty
          hours still reads healthy on /status; this line is what lets Mario
          judge "will this actually go out soon?" rather than trusting a green
          tile. See `approvalLiveness`. */}
      <p className={`pa-drain-chip pa-drain-${liveness.tone}`}>{liveness.label}</p>

      {/* Survives the refetch that removes the row it is about. */}
      {notice !== null ? <p className="pa-approval-notice">{notice}</p> : null}

      {error !== null ? <p className="flag-card-err">{error}</p> : null}
      {rows === null ? (
        <p className="empty">Loading the approval queue…</p>
      ) : pending.length === 0 ? (
        <p className="empty">Nothing staged for approval.</p>
      ) : (
        pending.map((row) => (
          <ApprovalCard
            key={row.id}
            row={row}
            lane={lane}
            now={now}
            canApprove={liveness.canApprove}
            onResolved={() => {
              setNotice(null);
              load();
              onResolved?.();
            }}
            onRaced={(message) => {
              setNotice(message);
              load();
              onResolved?.();
            }}
          />
        ))
      )}
    </div>
  );
}

function ApprovalCard({
  row,
  lane,
  now,
  canApprove,
  onResolved,
  onRaced,
}: {
  readonly row: PendingApprovalRow;
  readonly lane: ApprovalLane;
  readonly now: number;
  readonly canApprove: boolean;
  readonly onResolved: () => void;
  /**
   * Lost the race — report it UPWARD before refetching. The refetch is what
   * unmounts this card, so a message kept locally would be destroyed by the
   * very refresh that follows it.
   */
  readonly onRaced: (message: string) => void;
}): JSX.Element {
  const resolve = useDispatch<Record<string, unknown>, unknown>("resolve_pa_action");
  const feedback = useDispatch<Record<string, unknown>, unknown>("record_inbox_feedback");
  const [mode, setMode] = useState<"idle" | "approve" | "reject">("idle");
  const [note, setNote] = useState("");
  const [failure, setFailure] = useState<string | null>(null);

  const expired = isExpired(row, now);
  const preview = draftPreview(row.action, row.action_input);
  const threadId = parseInboxThreadRef(row.summary);
  const rejectBlocked = lane === "inbox" ? inboxRejectBlockedReason(row) : null;
  const busy = resolve.loading || feedback.loading;

  // A row with no token cannot be resolved at all — `resolve_pa_action` matches
  // by it. That only happens if `include_confirmation_token` was lost, so say
  // so rather than rendering buttons that 404.
  const token = row.confirmation_token;

  const doApprove = async (): Promise<void> => {
    setFailure(null);
    if (token === null) return;
    const r = await resolve.run(buildResolveInput(lane, token, "approve"));
    if (r.ok) {
      setMode("idle");
      onResolved();
      return;
    }
    // 409 is the ORDINARY case here, not a fault: someone tapped the Telegram
    // card between this page's fetch and this click. Both race codes go UP, so
    // the message outlives the refetch that removes this card.
    if (r.code === "pa_pending_action_invalid_state") {
      onRaced(
        "Someone got there first — that proposal had already been handled elsewhere, so nothing was queued from here.",
      );
      return;
    }
    if (r.code === "pa_pending_action_expired") {
      onRaced(
        "That approval window had already closed, so nothing was queued. Ask the agent to restage it.",
      );
      return;
    }
    setFailure(`${r.message} (${r.code})`);
  };

  const doReject = async (): Promise<void> => {
    setFailure(null);
    if (token === null || rejectBlocked !== null) return;
    const r = await resolve.run(buildResolveInput(lane, token, "reject"));
    if (!r.ok) {
      if (r.code === "pa_pending_action_invalid_state") {
        onRaced(
          "Someone got there first — that proposal had already been handled elsewhere, so your rejection changed nothing. If it was APPROVED, it is still queued to send.",
        );
        return;
      }
      setFailure(`${r.message} (${r.code})`);
      return;
    }
    // Peter's reject is legitimately terminal — his lane has no feedback loop.
    if (lane !== "inbox" || threadId === null) {
      setMode("idle");
      onResolved();
      return;
    }
    // The inbox reject is only HALF done at this point. A bare reject leaves
    // the matter at `awaiting_mario` with `next_action_at` untouched, so the
    // worker's due_before sweep never re-picks it — silent until a 48h drift
    // report that repairs nothing. If this second call fails the modal STAYS
    // OPEN with a retry, because closing it would hide a stalled matter.
    const f = await feedback.run({
      inbox_thread_id: threadId,
      feedback: note.trim(),
    });
    if (f.ok) {
      setMode("idle");
      setNote("");
      onResolved();
    } else {
      setFailure(
        `Rejected, but your note did NOT reach the agent (${f.code}). The matter will sit at awaiting_mario until it does — retry below.`,
      );
    }
  };

  return (
    <div className={`pa-approval-card${expired ? " pa-approval-expired" : ""}`}>
      <div className="pa-approval-head">
        <span className="tag">{row.action}</span>{" "}
        <span className="dc-muted">staged {humanAge(now - new Date(row.created_at).getTime())}</span>
        {expired ? <span className="pa-window-chip"> window closed</span> : null}
      </div>
      <p className="pa-approval-summary">{row.summary ?? "(no summary)"}</p>
      {row.reason !== null ? <p className="pr-sub dc-muted">Why: {row.reason}</p> : null}

      {preview !== null ? (
        <div className="pa-approval-draft">
          {preview.to !== null ? <div className="dc-muted">To: {preview.to}</div> : null}
          {preview.subject !== null ? <div>Subject: {preview.subject}</div> : null}
          <pre className="pa-approval-body">{preview.body}</pre>
        </div>
      ) : (
        // Never guess at a body we cannot read — approving something the panel
        // could not show is a decision better made on the Telegram card, where
        // the agent wrote the description itself.
        <p className="pr-sub dc-muted">
          No readable draft for this action — approve it from the Telegram card if you need
          to see what goes out.
        </p>
      )}

      <p className="pr-sub dc-muted">Window closes {fmt(row.expires_at)}</p>

      {failure !== null ? <p className="flag-card-err">{failure}</p> : null}

      {token === null ? (
        <p className="flag-card-err">
          This row came back without a confirmation token, so it cannot be resolved from
          here. Use the Telegram card.
        </p>
      ) : mode === "idle" ? (
        <div className="pr-engagement-actions">
          <button
            type="button"
            className="row-action-btn"
            disabled={!canApprove || expired || busy}
            title={
              expired
                ? "The approval window has closed — nothing would be queued"
                : !canApprove
                  ? "The drain is not reporting — an approval would not go out"
                  : "Approve this send"
            }
            onClick={() => setMode("approve")}
          >
            Approve
          </button>
          <button
            type="button"
            className="row-action-btn row-action-btn-danger"
            disabled={busy || rejectBlocked !== null}
            title={rejectBlocked ?? "Reject this draft"}
            onClick={() => setMode("reject")}
          >
            Reject
          </button>
        </div>
      ) : mode === "approve" ? (
        <div className="pa-approval-confirm">
          {/* A confirm step, not a click: this sends real mail and there is no
              undo — nothing can pull a row back out of `approved`. */}
          <p>
            This sends for real. It is queued for the drain&apos;s next beat and{" "}
            <strong>cannot be cancelled afterwards</strong>.
          </p>
          <button type="button" className="row-action-btn" disabled={busy} onClick={doApprove}>
            {busy ? "Approving…" : "Yes, send it"}
          </button>
          <button
            type="button"
            className="row-action-btn"
            disabled={busy}
            onClick={() => setMode("idle")}
          >
            Cancel
          </button>
        </div>
      ) : (
        <div className="pa-approval-confirm">
          {lane === "inbox" ? (
            <>
              <p>
                Rejecting alone leaves the matter waiting on you. Say what you&apos;d change
                and the agent redrafts it.
              </p>
              <textarea
                className="pa-approval-note"
                value={note}
                maxLength={2000}
                placeholder="what you'd change"
                onChange={(e) => setNote(e.target.value)}
              />
            </>
          ) : (
            <p>Rejecting is final for this draft — Peter will propose again if it&apos;s still worth doing.</p>
          )}
          <button
            type="button"
            className="row-action-btn row-action-btn-danger"
            // The note is REQUIRED on the inbox lane, and the button stays
            // disabled until it is non-empty — see `doReject`.
            disabled={busy || (lane === "inbox" && note.trim().length === 0)}
            onClick={doReject}
          >
            {busy ? "Rejecting…" : "Reject it"}
          </button>
          <button
            type="button"
            className="row-action-btn"
            disabled={busy}
            onClick={() => setMode("idle")}
          >
            Cancel
          </button>
          {!mayRecordFeedback(row) ? (
            // Unreachable from this panel today (it lists `status: "pending"`
            // only) and kept as a guard: `record_inbox_feedback` ignores
            // proposal status and the drain's re-check passes `revising`, so
            // feedback on an APPROVED row ships the original AND stages a
            // revision — two replies to the same person.
            <p className="flag-card-err">
              This proposal is already approved; feedback on it would produce a second reply.
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}
